import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Plus, ExternalLink, Heart, Settings, Trash2, ArrowLeft, Edit3, ShoppingBag, Gift, Grid3X3, List, ArrowUpDown, ChevronDown, Share2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { SEO } from '../components/common/SEO';
import { useAuth } from '../hooks/useAuth';
import { supabase, Wishlist } from '../services/supabase';
import { useGuestWishlists } from '../hooks/useGuestWishlists';
import { SkimlinksService } from '../services/skimlinks';
import { SimpleURLParser } from '../services/urlParser';
import { ClientSideScrapingService } from '../services/clientSideScrapingService';
import { PriceTrackingControls } from '../components/wishlist/PriceTrackingControls';
import toast from 'react-hot-toast';

/**
 * Modal component for editing items - defined outside main component to prevent re-creation
 */
const EditModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  title: string;
  children: React.ReactNode;
}> = ({ isOpen, onClose, onSave, title, children }) => {
  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      {/* Blurred backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>
      
      {/* Modal content */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="space-y-4">
            {children}
          </div>
          
          <div className="flex space-x-3 mt-6">
            <Button onClick={onSave} className="flex-1">
              Save Changes
            </Button>
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Share dropdown component
 */

export const WishlistView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { wishlists, getWishlistById, addItemToWishlist, removeItemFromWishlist, markItemAsPurchased, deleteWishlist, loading: guestWishlistsLoading } = useGuestWishlists();
  const [wishlist, setWishlist] = useState<Wishlist | null>(null);
  const [guestWishlist, setGuestWishlist] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [newItemUrl, setNewItemUrl] = useState('');
  const [newItemTitle, setNewItemTitle] = useState('');
  const [newItemPrice, setNewItemPrice] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    title: '',
    price: '',
    currency: 'USD',
    image: ''
  });
  const [sortBy, setSortBy] = useState<'default' | 'price-high' | 'price-low' | 'alphabetical-az' | 'alphabetical-za'>('default');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isSharing, setIsSharing] = useState(false);
  const priceInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!id) return;

    // For guest wishlists, wait until guestWishlists are loaded
    if (id.startsWith('guest_')) {
      // Only fetch if guest wishlists are not loading
      if (!guestWishlistsLoading) {
        fetchWishlist();
      }
    } else {
      // For authenticated wishlists, fetch immediately
      fetchWishlist();
    }
  }, [id, user, guestWishlistsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchWishlist = async () => {
    if (!id) return;

    setLoading(true);
    try {
      if (id.startsWith('guest_')) {
        // Guest wishlist - get from localStorage
        const guestWishlistData = getWishlistById(id);
        
        if (guestWishlistData) {
          setGuestWishlist(guestWishlistData);
        } else {
          // Only show error if we haven't already set a guest wishlist
          if (!guestWishlist) {
            toast.error('Guest wishlist not found');
          }
        }
      } else {
        // Authenticated wishlist - get from database
        if (!user) {
          toast.error('Please sign in to view this wishlist');
          return;
        }
        
        const { data, error } = await supabase
          .from('wishlists')
          .select(`
            *,
            items:wishlist_items(*)
          `)
          .eq('id', id)
          .single();

        if (error) {
          if (error.code === 'PGRST116') {
            toast.error('Wishlist not found');
          } else {
            throw error;
          }
        } else {
          setWishlist(data);
        }
      }
    } catch (error: any) {
      console.error('Error fetching wishlist:', error);
      toast.error(error.message || 'Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItemUrl.trim()) return;

    setIsAddingItem(true);
    try {
      // Show scraping progress
      toast.loading('Scraping product information...', { id: 'scraping' });

      // Try client-side scraping first
      let productData;
      try {
        console.log('🔍 Starting scraping for:', newItemUrl);
        productData = await ClientSideScrapingService.scrapeProduct(newItemUrl);
        console.log('✅ Scraping successful:', productData);
        toast.success('Product information extracted!', { id: 'scraping' });
      } catch (error) {
        console.warn('⚠️ Scraping failed, trying fallback:', error);
        // Fallback to simple parser
        toast.loading('Trying alternative method...', { id: 'scraping' });
        productData = await SimpleURLParser.parseURL(newItemUrl);
        console.log('🔄 Fallback result:', productData);
        toast.success('Basic product information extracted', { id: 'scraping' });
      }

      // Convert to affiliate link
      const affiliateUrl = SkimlinksService.convertToAffiliate(newItemUrl);

      console.log('🔄 Final product data before adding:', {
        title: newItemTitle || productData.title,
        price: newItemPrice ? parseFloat(newItemPrice) : productData.price,
        image: productData.image,
        currency: productData.currency,
        originalUrl: newItemUrl,
        affiliateUrl
      });

      if (id?.startsWith('guest_')) {
        // Guest wishlist - add to localStorage
        addItemToWishlist(
          id,
          newItemTitle || productData.title,
          newItemUrl,
          newItemPrice ? parseFloat(newItemPrice) : productData.price,
          productData.image,
          affiliateUrl,
          productData.currency
        );
        // Update the local state immediately with the new item
        const newItem = {
          id: `item_${Date.now()}`,
          title: newItemTitle || productData.title,
          original_url: newItemUrl,
          affiliate_url: affiliateUrl,
          price: newItemPrice ? parseFloat(newItemPrice) : productData.price,
          image_url: productData.image,
          currency: productData.currency || 'USD',
          is_purchased: false,
          created_at: new Date().toISOString()
        };
        setGuestWishlist((prev: any) => {
          if (!prev) return null;
          const updated = {
            ...prev,
            items: [...prev.items, newItem]
          };
          console.log('📝 Updated guest wishlist:', updated);
          return updated;
        });
        console.log('✅ New item created:', newItem);
        toast.success('Item added to wishlist!');
      } else if (wishlist) {
        // Authenticated wishlist - save to database
        const { data, error } = await supabase
          .from('wishlist_items')
          .insert({
            wishlist_id: wishlist.id,
            title: newItemTitle || productData.title,
            original_url: newItemUrl,
            affiliate_url: affiliateUrl,
            price: newItemPrice ? parseFloat(newItemPrice) : productData.price,
            image_url: productData.image,
            description: productData.description,
            brand: productData.brand,
            currency: productData.currency || 'USD'
          })
          .select()
          .single();

        if (error) throw error;

        // Update local state
        setWishlist(prev => prev ? {
          ...prev,
          items: [...(prev.items || []), data]
        } : null);

        toast.success('Item added to wishlist!');
      }

      // Reset form
      setNewItemUrl('');
      setNewItemTitle('');
      setNewItemPrice('');
      setShowAddForm(false);
    } catch (error: any) {
      console.error('Error adding item:', error);
      toast.error(error.message || 'Failed to add item', { id: 'scraping' });
    } finally {
      setIsAddingItem(false);
    }
  };

  const formatPrice = (price: number | null | undefined, currency?: string, originalUrl?: string) => {
    if (!price) return 'Price not available';
    if (!currency) return `$${price.toFixed(2)}`;
    
    const currencySymbols: { [key: string]: string } = {
      'USD': '$',
      'AED': 'AED ',
      'EUR': '€',
      'GBP': '£',
      'CAD': 'C$',
      'AUD': 'A$',
      'JPY': '¥',
      'INR': '₹',
      'SGD': 'S$'
    };
    
    const symbol = currencySymbols[currency] || currency + ' ';
    return `${symbol}${price.toFixed(2)}`;
  };

  /**
   * Check if a URL is from a site known to have dynamic pricing that blocks scraping
   */
  const isDynamicPricingSite = (url: string): boolean => {
    const dynamicSites = [
      'target.com',
      'walmart.com',
      'bestbuy.com',
      'homedepot.com',
      'lowes.com',
      'costco.com'
    ];
    
    return dynamicSites.some(site => url.toLowerCase().includes(site));
  };

  /**
   * Get tooltip message for price not available
   */
  const getPriceTooltipMessage = (originalUrl?: string): string => {
    if (originalUrl && isDynamicPricingSite(originalUrl)) {
      return 'Price loaded dynamically - use edit to add manually';
    }
    return 'Price not available - use edit to add manually';
  };

  /**
   * Sort items based on selected sort option
   */
  const sortItems = (items: any[]) => {
    if (!items || items.length === 0) return items;

    const sortedItems = [...items];

    switch (sortBy) {
      case 'price-high':
        return sortedItems.sort((a, b) => {
          const priceA = a.price || 0;
          const priceB = b.price || 0;
          return priceB - priceA;
        });
      
      case 'price-low':
        return sortedItems.sort((a, b) => {
          const priceA = a.price || 0;
          const priceB = b.price || 0;
          return priceA - priceB;
        });
      
      case 'alphabetical-az':
        return sortedItems.sort((a, b) => {
          const titleA = a.title?.toLowerCase() || '';
          const titleB = b.title?.toLowerCase() || '';
          return titleA.localeCompare(titleB);
        });
      
      case 'alphabetical-za':
        return sortedItems.sort((a, b) => {
          const titleA = a.title?.toLowerCase() || '';
          const titleB = b.title?.toLowerCase() || '';
          return titleB.localeCompare(titleA);
        });
      
      default:
        return sortedItems; // Keep original order
    }
  };

  const Tooltip: React.FC<{ 
    children: React.ReactNode; 
    message: string; 
    className?: string;
  }> = ({ children, message, className = '' }) => {
    const [isVisible, setIsVisible] = useState(false);

    return (
      <div 
        className={`relative inline-block ${className}`}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
        {isVisible && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg whitespace-nowrap z-50">
            {message}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
          </div>
        )}
      </div>
    );
  };
  const ProductImagePlaceholder: React.FC<{ className?: string }> = ({ className = '' }) => (
    <div className={`bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center ${className}`}>
      <Gift className="h-8 w-8 text-primary-400" />
    </div>
  );

  /**
   * Product image component with fallback
   */
  const ProductImage: React.FC<{ 
    src?: string | null; 
    alt: string; 
    className?: string;
    fallbackClassName?: string;
  }> = ({ src, alt, className = '', fallbackClassName = '' }) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);

    // Reset states when src changes
    useEffect(() => {
      setImageError(false);
      setImageLoading(true);
    }, [src]);

    if (!src || imageError) {
      return <ProductImagePlaceholder className={`${className} ${fallbackClassName}`} />;
    }

    return (
      <div className={`relative ${className}`}>
        {imageLoading && (
          <div className="absolute inset-0 bg-gray-100 animate-pulse flex items-center justify-center">
            <Gift className="h-6 w-6 text-gray-400" />
          </div>
        )}
        <img
          src={src}
          alt={alt}
          className={`w-full h-full object-cover ${imageLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-200`}
          onLoad={() => setImageLoading(false)}
          onError={() => {
            setImageError(true);
            setImageLoading(false);
          }}
        />
      </div>
    );
  };

  const handleRemoveItem = async (itemId: string) => {
    try {
      if (id?.startsWith('guest_')) {
        // Guest wishlist - remove from localStorage
        removeItemFromWishlist(itemId, id);
        // Update the local state immediately by filtering out the removed item
        setGuestWishlist((prev: any) => {
          if (!prev) return null;
          return {
            ...prev,
            items: prev.items.filter((item: any) => item.id !== itemId)
          };
        });
        toast.success('Item removed!');
      } else if (wishlist) {
        // Authenticated wishlist - remove from database
        const { error } = await supabase
          .from('wishlist_items')
          .delete()
          .eq('id', itemId);

        if (error) throw error;

        setWishlist(prev => prev ? {
          ...prev,
          items: prev.items?.filter(item => item.id !== itemId) || []
        } : null);

        toast.success('Item removed!');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to remove item');
    }
  };

  const handleMarkAsPurchased = async (itemId: string, purchased: boolean) => {
    try {
      if (id?.startsWith('guest_')) {
        // Guest wishlist - update in localStorage
        markItemAsPurchased(itemId, id, purchased);
        // Update the local state immediately
        setGuestWishlist((prev: any) => {
          if (!prev) return null;
          return {
            ...prev,
            items: prev.items.map((item: any) =>
              item.id === itemId ? { ...item, is_purchased: purchased } : item
            )
          };
        });
        toast.success(`Item marked as ${purchased ? 'purchased' : 'available'}!`);
      } else if (wishlist) {
        // Authenticated wishlist - update in database
        const { error } = await supabase
          .from('wishlist_items')
          .update({ is_purchased: purchased })
          .eq('id', itemId);

        if (error) throw error;

        setWishlist(prev => prev ? {
          ...prev,
          items: prev.items?.map(item => 
            item.id === itemId ? { ...item, is_purchased: purchased } : item
          ) || []
        } : null);

        toast.success(`Item marked as ${purchased ? 'purchased' : 'available'}!`);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update item status');
    }
  };

  const handleAffiliateClick = async (item: any) => {
    try {
      if (id?.startsWith('guest_')) {
        // For guest users, just open the link
        window.open(item.affiliate_url || item.original_url, '_blank');
      } else {
        // For authenticated users, track the click
        await SkimlinksService.trackClick(item.id, item.original_url, item.title);
        window.open(item.affiliate_url || item.original_url, '_blank');
      }
    } catch (error) {
      console.error('Failed to track click:', error);
      window.open(item.original_url, '_blank');
    }
  };

  const handleDeleteWishlist = async () => {
    if (!currentWishlist || !isOwner) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete "${currentWishlist.title}"? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      if (id?.startsWith('guest_')) {
        // Guest wishlist - delete from localStorage
        deleteWishlist(id);
        toast.success('Wishlist deleted!');
        navigate('/dashboard');
      } else if (wishlist) {
        // Authenticated wishlist - delete from database
        const { error } = await supabase
          .from('wishlists')
          .delete()
          .eq('id', wishlist.id);

        if (error) throw error;
        toast.success('Wishlist deleted!');
        navigate('/dashboard');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete wishlist');
    }
  };

  const handleEditItem = (item: any) => {
    setEditingItem(item.id);
    setEditForm({
      title: item.title,
      price: item.price?.toString() || '',
      currency: item.currency || 'USD',
      image: item.image_url || ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;

    try {
      const updatedData = {
        title: editForm.title,
        price: editForm.price ? parseFloat(editForm.price) : undefined,
        currency: editForm.currency,
        image_url: editForm.image || undefined
      };

      if (id?.startsWith('guest_')) {
        // Guest wishlist - update in localStorage
        const updatedWishlists = wishlists.map(wishlist =>
          wishlist.id === id
            ? {
                ...wishlist,
                items: wishlist.items.map((item: any) =>
                  item.id === editingItem ? { ...item, ...updatedData } : item
                )
              }
            : wishlist
        );
        
        // Update local storage
        localStorage.setItem('wishful_guest_wishlists', JSON.stringify(updatedWishlists));
        
        // Update local state
        setGuestWishlist((prev: any) => {
          if (!prev) return null;
          return {
            ...prev,
            items: prev.items.map((item: any) =>
              item.id === editingItem ? { ...item, ...updatedData } : item
            )
          };
        });
        
        toast.success('Item updated!');
      } else if (wishlist) {
        // Authenticated wishlist - update in database
        const { error } = await supabase
          .from('wishlist_items')
          .update(updatedData)
          .eq('id', editingItem);

        if (error) throw error;

        // Update local state
        setWishlist(prev => prev ? {
          ...prev,
          items: prev.items?.map(item =>
            item.id === editingItem ? { ...item, ...updatedData } : item
          ) || []
        } : null);
        
        toast.success('Item updated!');
      }

      setEditingItem(null);
      setEditForm({ title: '', price: '', currency: 'USD', image: '' });
    } catch (error: any) {
      toast.error(error.message || 'Failed to update item');
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditForm({ title: '', price: '', currency: 'USD', image: '' });
  };

  const handlePriceChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEditForm(prev => ({ ...prev, price: e.target.value }));
  }, []);

  // Focus price input when editing starts
  useEffect(() => {
    if (editingItem && priceInputRef.current) {
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        priceInputRef.current?.focus();
      }, 100);
    }
  }, [editingItem]);

  // Share wishlist functionality using Web Share API
  const handleShareWishlist = async () => {
    if (!currentWishlist) return;
    
    setIsSharing(true);
    
    try {
      const wishlistUrl = `${window.location.origin}/wishlist/${id}`;
      const shareText = `Check out my wishlist: ${currentWishlist.title}`;
      
      // Check if Web Share API is available (mobile devices)
      if (navigator.share) {
        await navigator.share({
          title: shareText,
          text: shareText,
          url: wishlistUrl,
        });
        toast.success('Wishlist shared!');
      } else {
        // Fallback to clipboard copy for desktop
        await navigator.clipboard.writeText(wishlistUrl);
        toast.success('Wishlist link copied to clipboard!');
      }
    } catch (error) {
      console.error('Failed to share wishlist:', error);
      
      // If sharing was cancelled by user, don't show error
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      
      // Try clipboard as fallback
      try {
        const wishlistUrl = `${window.location.origin}/wishlist/${id}`;
        await navigator.clipboard.writeText(wishlistUrl);
        toast.success('Wishlist link copied to clipboard!');
      } catch (clipboardError) {
        toast.error('Failed to share. Please try again.');
      }
    } finally {
      setIsSharing(false);
    }
  };

  // Handle price updates from price tracking
  const handlePriceUpdate = async (itemId: string, newPrice: number) => {
    try {
      if (user) {
        // Update in database for authenticated users
        const { error } = await supabase
          .from('wishlist_items')
          .update({ price: newPrice })
          .eq('id', itemId);

        if (error) throw error;
      } else {
        // Update in localStorage for guest users
        if (guestWishlist) {
          const updatedItems = guestWishlist.items.map((item: any) =>
            item.id === itemId ? { ...item, price: newPrice } : item
          );
          setGuestWishlist({ ...guestWishlist, items: updatedItems });
        }
      }
    } catch (error) {
      console.error('Failed to update price:', error);
      toast.error('Failed to update price');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const currentWishlist = wishlist || guestWishlist;
  if (!currentWishlist) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="text-center py-12">
          <Heart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Wishlist not found
          </h2>
          <p className="text-gray-600 mb-4">
            This wishlist doesn't exist or you don't have permission to view it.
          </p>
          <Link to="/dashboard">
            <Button>Back to Dashboard</Button>
          </Link>
        </Card>
      </div>
    );
  }

  const isOwner = user?.id === currentWishlist.user_id || id?.startsWith('guest_');
  const purchasedItems = currentWishlist.items?.filter((item: any) => item.is_purchased) || [];
  const availableItems = currentWishlist.items?.filter((item: any) => !item.is_purchased) || [];

  return (
    <>
      <SEO
        title={currentWishlist.title}
        description={currentWishlist.description || `View ${currentWishlist.title} wishlist with ${currentWishlist.items?.length || 0} items`}
      />

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            {/* Back Button */}
            <div className="mb-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Wishlists
              </Button>
            </div>

            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {currentWishlist.title}
                </h1>
                {currentWishlist.description && (
                  <p className="text-gray-600">{currentWishlist.description}</p>
                )}
              </div>
              
              {isOwner && (
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleShareWishlist}
                    loading={isSharing}
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </Button>
                  <Button variant="outline" size="sm">
                    <Settings className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleDeleteWishlist}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </div>
              )}
              
              {!isOwner && (
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleShareWishlist}
                    loading={isSharing}
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </Button>
                  <Button variant="outline" size="sm">
                    <Settings className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-6 text-sm text-gray-600">
              <span>{currentWishlist.items?.length || 0} total items</span>
              <span>{availableItems.length} available</span>
              <span>{purchasedItems.length} purchased</span>
              <span>{currentWishlist.is_public ? 'Public' : 'Private'}</span>
            </div>
          </div>

          {/* Add Item Form */}
          {isOwner && (
            <Card className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Add New Item</h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddForm(!showAddForm)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {showAddForm ? 'Cancel' : 'Add Item'}
                </Button>
              </div>

              {showAddForm && (
                <form onSubmit={handleAddItem} className="space-y-4">
                  <div>
                    <Input
                      label="Product URL"
                      value={newItemUrl}
                      onChange={(e) => setNewItemUrl(e.target.value)}
                      placeholder="Paste any product URL (Amazon, Target, etc.)"
                      required
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      ✨ We'll automatically extract product information, images, and prices
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Product Title (Optional)"
                      value={newItemTitle}
                      onChange={(e) => setNewItemTitle(e.target.value)}
                      placeholder="Will be auto-detected from URL"
                    />
                    <Input
                      label="Price (Optional)"
                      type="number"
                      step="0.01"
                      value={newItemPrice}
                      onChange={(e) => setNewItemPrice(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <Button type="submit" loading={isAddingItem} className="w-full">
                    {isAddingItem ? 'Scraping Product...' : 'Add to Wishlist'}
                  </Button>
                </form>
              )}
            </Card>
          )}

          {/* Sort and View Controls */}
          {availableItems.length > 0 && (
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <ArrowUpDown className="h-4 w-4 text-gray-500" />
                  <label className="text-sm font-medium text-gray-700">Sort by:</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="default">Default</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="alphabetical-az">A-Z</option>
                    <option value="alphabetical-za">Z-A</option>
                  </select>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">View:</span>
                <div className="flex border border-gray-300 rounded-md overflow-hidden">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`px-3 py-1 text-sm flex items-center gap-1 ${
                      viewMode === 'grid' 
                        ? 'bg-primary-500 text-white' 
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Grid3X3 className="h-4 w-4" />
                    Grid
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-3 py-1 text-sm flex items-center gap-1 ${
                      viewMode === 'list' 
                        ? 'bg-primary-500 text-white' 
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <List className="h-4 w-4" />
                    List
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Items Display */}
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' 
            : 'space-y-4'
          }>
            {sortItems(availableItems).map((item: any) => (
              <div key={item.id}>
                {viewMode === 'grid' ? (
                  <Card hover>
                    <ProductImage
                      src={item.image_url}
                      alt={item.title}
                      className="w-full h-48 rounded-lg mb-4"
                    />
                    <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                      {item.title}
                    </h3>
                    <div className="text-lg font-semibold text-green-600 mb-3">
                      {!item.price ? (
                        <Tooltip message={getPriceTooltipMessage(item.original_url)}>
                          <span className="text-gray-500 cursor-help">
                            {formatPrice(item.price, item.currency, item.original_url)}
                          </span>
                        </Tooltip>
                      ) : (
                        formatPrice(item.price, item.currency, item.original_url)
                      )}
                    </div>
                    
                    {/* Price Tracking Controls - Only for authenticated users */}
                    {user && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <PriceTrackingControls
                          itemId={item.id}
                          currentPrice={item.price}
                          currency={item.currency}
                          priceTrackingEnabled={item.price_tracking_enabled || false}
                          trackingPriority={item.tracking_priority || 'LOW'}
                          lastPriceCheck={item.last_price_check}
                          onPriceUpdate={(newPrice) => handlePriceUpdate(item.id, newPrice)}
                        />
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <Button
                        size="sm"
                        onClick={() => handleAffiliateClick(item)}
                        className="flex-1 mr-2"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Product
                      </Button>
                      {isOwner && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditItem(item)}
                            className="mr-2"
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMarkAsPurchased(item.id, true)}
                          >
                            <ShoppingBag className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                    {isOwner && (
                      <button
                        onClick={() => handleRemoveItem(item.id)}
                        className="mt-2 text-red-600 hover:text-red-800 text-sm"
                      >
                        <Trash2 className="h-4 w-4 inline mr-1" />
                        Remove
                      </button>
                    )}
                  </Card>
                ) : (
                  <Card hover className="flex items-center gap-4 p-4">
                    <ProductImage
                      src={item.image_url}
                      alt={item.title}
                      className="w-20 h-20 rounded-lg flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-lg mb-1 line-clamp-1">
                        {item.title}
                      </h3>
                      <div className="text-lg font-semibold text-green-600 mb-2">
                        {!item.price ? (
                          <Tooltip message={getPriceTooltipMessage(item.original_url)}>
                            <span className="text-gray-500 cursor-help">
                              {formatPrice(item.price, item.currency, item.original_url)}
                            </span>
                          </Tooltip>
                        ) : (
                          formatPrice(item.price, item.currency, item.original_url)
                        )}
                      </div>
                      
                      {/* Price Tracking Controls - Only for authenticated users */}
                      {user && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <PriceTrackingControls
                            itemId={item.id}
                            currentPrice={item.price}
                            currency={item.currency}
                            priceTrackingEnabled={item.price_tracking_enabled || false}
                            trackingPriority={item.tracking_priority || 'LOW'}
                            lastPriceCheck={item.last_price_check}
                            onPriceUpdate={(newPrice) => handlePriceUpdate(item.id, newPrice)}
                          />
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleAffiliateClick(item)}
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        {isOwner && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditItem(item)}
                            >
                              <Edit3 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleMarkAsPurchased(item.id, true)}
                            >
                              <ShoppingBag className="h-4 w-4" />
                            </Button>
                            <button
                              onClick={() => handleRemoveItem(item.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </Card>
                )}
              </div>
            ))}
          </div>

          {/* Edit Modal */}
          <EditModal
            isOpen={editingItem !== null}
            onClose={handleCancelEdit}
            onSave={handleSaveEdit}
            title="Edit Item"
          >
            <Input
              key={`title-${editingItem}`}
              label="Title"
              value={editForm.title}
              onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
            />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                <input
                  ref={priceInputRef}
                  type="number"
                  value={editForm.price}
                  onChange={handlePriceChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select
                  key={`currency-${editingItem}`}
                  value={editForm.currency}
                  onChange={(e) => setEditForm(prev => ({ ...prev, currency: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="USD">USD</option>
                  <option value="AED">AED</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="CAD">CAD</option>
                  <option value="AUD">AUD</option>
                  <option value="JPY">JPY</option>
                  <option value="INR">INR</option>
                  <option value="SGD">SGD</option>
                </select>
              </div>
            </div>
            <Input
              key={`image-${editingItem}`}
              label="Image URL"
              value={editForm.image}
              onChange={(e) => setEditForm(prev => ({ ...prev, image: e.target.value }))}
              placeholder="https://example.com/image.jpg"
            />
          </EditModal>

          {/* Purchased Items */}
          {purchasedItems.length > 0 && (
            <div className="mt-12">
              <h2 className="text-xl font-semibold mb-4">Purchased Items</h2>
              <div className={viewMode === 'grid' 
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' 
                : 'space-y-4'
              }>
                {sortItems(purchasedItems).map((item: any) => (
                  <div key={item.id}>
                    {viewMode === 'grid' ? (
                      <Card className="opacity-75">
                        <ProductImage
                          src={item.image_url}
                          alt={item.title}
                          className="w-full h-48 rounded-lg mb-4"
                        />
                        <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                          {item.title}
                        </h3>
                        <div className="text-lg font-semibold text-gray-500 mb-3">
                          {!item.price ? (
                            <Tooltip message={getPriceTooltipMessage(item.original_url)}>
                              <span className="text-gray-400 cursor-help">
                                {formatPrice(item.price, item.currency, item.original_url)}
                              </span>
                            </Tooltip>
                          ) : (
                            formatPrice(item.price, item.currency, item.original_url)
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-green-600 font-semibold flex items-center">
                            <ShoppingBag className="h-4 w-4 mr-1" />
                            Purchased
                          </span>
                          {isOwner && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleMarkAsPurchased(item.id, false)}
                            >
                              Undo
                            </Button>
                          )}
                        </div>
                      </Card>
                    ) : (
                      <Card className="opacity-75 flex items-center gap-4 p-4">
                        <ProductImage
                          src={item.image_url}
                          alt={item.title}
                          className="w-20 h-20 rounded-lg flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-lg mb-1 line-clamp-1">
                            {item.title}
                          </h3>
                          <div className="text-lg font-semibold text-gray-500 mb-2">
                            {!item.price ? (
                              <Tooltip message={getPriceTooltipMessage(item.original_url)}>
                                <span className="text-gray-400 cursor-help">
                                  {formatPrice(item.price, item.currency, item.original_url)}
                                </span>
                              </Tooltip>
                            ) : (
                              formatPrice(item.price, item.currency, item.original_url)
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-green-600 font-semibold flex items-center text-sm">
                              <ShoppingBag className="h-4 w-4 mr-1" />
                              Purchased
                            </span>
                            {isOwner && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleMarkAsPurchased(item.id, false)}
                              >
                                Undo
                              </Button>
                            )}
                          </div>
                        </div>
                      </Card>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {currentWishlist.items?.length === 0 && (
            <Card className="text-center py-12">
              <Heart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No items yet
              </h3>
              <p className="text-gray-600">
                {isOwner ? 'Add your first item to get started!' : 'This wishlist is empty.'}
              </p>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};
